import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LanguageChooser from '@/components/LanguageChooser/LanguageChooser';
import { EshkolLogo } from '@/components/EshkolLogo';
import { ProductLogo } from '@/components/ProductLogo';
import { AppBanner, Card } from '@/components/ui';
import { useLocale } from '@/i18n/LocaleProvider';
import { useTranslation } from '@/i18n/useTranslation';
import { createSession } from '@/lib/api/sessions';
import { prefetchMapConfig } from '@/lib/map/map-cache';
import { trackIntakeStarted } from '@/lib/analytics';
import { seedWizardSession, clearWizardSession } from '@/context/WizardSessionContext';
import { clearAssessmentPlanningCache } from '@/lib/assessment-planning-cache';
import { JOURNEY_STEP } from '@/lib/journey-steps';
import styles from './LandingScreen.module.css';

type LandingLocationState = { intakeSubmitSuccess?: boolean };

export default function LandingScreen() {
  const t = useTranslation();
  const { language } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showIntakeSuccess, setShowIntakeSuccess] = useState(
    () => Boolean((location.state as LandingLocationState | null)?.intakeSubmitSuccess),
  );

  useEffect(() => {
    if (!(location.state as LandingLocationState | null)?.intakeSubmitSuccess) return;
    setShowIntakeSuccess(true);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  const handleStart = async () => {
    setLoading(true);
    setError(null);
    setShowIntakeSuccess(false);
    try {
      prefetchMapConfig();
      const session = await createSession({ client_meta: { locale: language } });
      clearWizardSession();
      clearAssessmentPlanningCache();
      seedWizardSession(session.id, JOURNEY_STEP.LANDING, {});
      trackIntakeStarted({ locale: language, source: 'landing' });
      navigate('/map');
    } catch {
      setError(t.landing.startError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page} data-testid="landing-screen">
      <div className={styles.pageBody}>
        <Card className={styles.onboardPanel}>
          <div className={styles.layout}>
            <div className={styles.main}>
              <header className={styles.brandBlock}>
                <ProductLogo variant="full" />
                <div className={styles.partnerBrand}>
                  <EshkolLogo alt={t.landing.eshkolLogoAlt} variant="hero" />
                </div>
              </header>

              <div className={styles.languageBar}>
                <LanguageChooser />
              </div>

              <div className={styles.copy}>
                <h1 className={styles.heading}>{t.landing.title}</h1>
                <p className={styles.subheading}>{t.landing.subtitle}</p>
                {showIntakeSuccess ? (
                  <AppBanner
                    variant="success"
                    title={t.landing.intakeSuccessTitle}
                    text={t.landing.intakeSuccessBody}
                    className={styles.successBanner}
                    data-testid="landing-intake-success"
                  />
                ) : null}
                {error ? (
                  <p className={styles.error} role="alert">
                    {error}
                  </p>
                ) : null}
              </div>

              <div className={styles.formActions}>
                <button
                  type="button"
                  className={styles.cta}
                  onClick={() => void handleStart()}
                  disabled={loading}
                  data-testid="landing-start-button"
                >
                  {loading ? t.landing.starting : t.landing.startCta}
                </button>
              </div>

              <footer className={styles.footerLinks}>
                <a
                  className={styles.footerLink}
                  href={t.landing.privacyHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="landing-privacy-link"
                >
                  {t.landing.privacyLink}
                </a>
                <span className={styles.footerSep} aria-hidden>
                  ·
                </span>
                <a className={styles.footerLink} href="/docs/">
                  {t.header.docs}
                </a>
              </footer>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
